'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { Flag, AlertTriangle, CheckCircle, AlertCircle, Search, Shield, ExternalLink } from 'lucide-react';

const reportReasons = [
  {
    value: 'counterfeit',
    label: 'Counterfeit or Fake Item',
    description: 'The item appears to be a replica, fake, or counterfeit version of a branded product.',
  },
  {
    value: 'stolen',
    label: 'Stolen Goods',
    description: 'I believe this item may be stolen or obtained through fraud.',
  },
  {
    value: 'prohibited',
    label: 'Prohibited Item',
    description: 'This item is not allowed on DrivenMarket (e.g. illegal weapons, banned equipment).',
  },
  {
    value: 'animal_welfare',
    label: 'Animal Welfare Concern',
    description: 'This listing may violate animal welfare laws, Lucy\'s Law, or breeding regulations.',
  },
  {
    value: 'misleading',
    label: 'Misleading or Inaccurate Listing',
    description: 'The description, photos, or price are deliberately misleading or inaccurate.',
  },
  {
    value: 'fraud',
    label: 'Suspected Fraud or Scam',
    description: 'I believe this listing is designed to defraud buyers.',
  },
  {
    value: 'off_platform',
    label: 'Soliciting Off-Platform Payment',
    description: 'The seller is asking buyers to pay outside of DrivenMarket.',
  },
  {
    value: 'duplicate',
    label: 'Duplicate Listing',
    description: 'This item has been listed multiple times by the same seller.',
  },
  {
    value: 'inappropriate',
    label: 'Inappropriate Content',
    description: 'The listing contains offensive, inappropriate, or unrelated content.',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Something else is wrong with this listing. Please describe it below.',
  },
];

function ReportPageContent() {
  const searchParams = useSearchParams();
  const listingIdParam = searchParams?.get('listing_id') || '';

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [listingId, setListingId] = useState(listingIdParam);
  const [listingSearch, setListingSearch] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportRef, setReportRef] = useState('');

  // If listing_id is in URL, skip to step 2
  useEffect(() => {
    if (listingIdParam) {
      setListingId(listingIdParam);
      setStep(2);
    }
  }, [listingIdParam]);

  const handleStep1 = () => {
    const id = listingId.trim() || listingSearch.trim();
    if (!id) {
      setError('Please enter a listing ID or URL.');
      return;
    }
    // Extract UUID from URL if pasted
    const uuidMatch = id.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    if (uuidMatch) {
      setListingId(uuidMatch[0]);
    } else {
      setListingId(id);
    }
    setError(null);
    setStep(2);
  };

  const handleStep2 = () => {
    if (!selectedReason) {
      setError('Please select a reason for your report.');
      return;
    }
    setError(null);
    setStep(3);
  };

  const handleSubmit = async () => {
    if (details.trim().length < 10) {
      setError('Please provide at least a brief description of the issue.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      const { data, error: dbError } = await supabase
        .from('listing_reports')
        .insert({
          listing_id: listingId || null,
          reporter_id: user?.id || null,
          reason: selectedReason,
          details: details.trim(),
          status: 'pending',
        })
        .select('id')
        .single();

      if (dbError) throw dbError;

      // Generate a short reference from the UUID
      const ref = data?.id ? `RPT-${data.id.slice(0, 8).toUpperCase()}` : 'RPT-SUBMITTED';
      setReportRef(ref);
      setSubmitted(true);
    } catch (err: unknown) {
      console.error(err);
      setError('Failed to submit your report. Please try again or email trust@drivenmarket.co.uk.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[hsl(222,47%,6%)]">
        <Topbar />
        <main className="pt-16">
          <div className="max-w-lg mx-auto px-4 lg:px-8 py-20 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-400/10 mb-5">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">Report Submitted</h1>
            <p className="text-slate-400 text-sm leading-relaxed mb-2">
              Thank you for helping keep DrivenMarket safe. Our trust team will review your report within 24 hours.
            </p>
            {reportRef && (
              <p className="text-slate-500 text-xs mb-6">
                Reference: <span className="text-amber-400 font-mono font-semibold">{reportRef}</span>
              </p>
            )}
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-5 text-left mb-6 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">What happens next</p>
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm">Our team reviews all reports within 24 hours.</p>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm">If the listing violates our guidelines, it will be removed and the seller notified.</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm">For urgent safety concerns, email <a href="mailto:trust@drivenmarket.co.uk" className="text-amber-400 hover:text-amber-300">trust@drivenmarket.co.uk</a> directly.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/listings-feed" className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold rounded-lg transition-colors">
                Back to Listings
              </Link>
              <button
                onClick={() => { setSubmitted(false); setStep(1); setListingId(''); setSelectedReason(''); setDetails(''); setListingSearch(''); }}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Submit Another Report
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(222,47%,6%)]">
      <Topbar />
      <main className="pt-16">
        {/* Hero */}
        <div className="bg-gradient-to-b from-[hsl(222,47%,8%)] to-[hsl(222,47%,6%)] border-b border-[hsl(222,25%,18%)]">
          <div className="max-w-2xl mx-auto px-4 lg:px-8 py-12 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-400/10 mb-5">
              <Flag className="w-7 h-7 text-rose-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Report a Listing</h1>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mx-auto">
              Help us keep DrivenMarket safe. Reports are reviewed by our trust team within 24 hours. All reports are anonymous.
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-8 sm:py-10">

          {/* Progress Steps */}
          <div className="flex items-center gap-1 sm:gap-2 mb-8">
            {[
              { n: 1, label: 'Identify Listing' },
              { n: 2, label: 'Select Reason' },
              { n: 3, label: 'Add Details' },
            ].map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${step >= s.n ? 'bg-amber-400 text-black' : 'bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] text-slate-500'}`}>
                    {step > s.n ? <CheckCircle className="w-4 h-4" /> : s.n}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block truncate ${step >= s.n ? 'text-white' : 'text-slate-500'}`}>{s.label}</span>
                </div>
                {i < 2 && <div className={`flex-1 h-px min-w-[12px] ${step > s.n ? 'bg-amber-400/40' : 'bg-[hsl(222,25%,18%)]'}`} />}
              </React.Fragment>
            ))}
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-lg mb-5">
              <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
              <p className="text-rose-300 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Identify Listing */}
          {step === 1 && (
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-4 sm:p-6 space-y-5">
              <div>
                <h2 className="text-base font-semibold text-white mb-1">Which listing are you reporting?</h2>
                <p className="text-slate-400 text-xs">Paste the listing URL or ID below. You can find the listing ID in the URL of the listing page.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Listing URL or ID</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={listingSearch}
                    onChange={(e) => { setListingSearch(e.target.value); setError(null); }}
                    placeholder="https://drivenmark9994.builtwithrocket.new/listing-detail?id=…"
                    className="w-full bg-[hsl(222,47%,6%)] border border-[hsl(222,25%,18%)] rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-400/50 transition-colors"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-[hsl(222,25%,18%)]" />
                <span className="text-xs text-slate-600">or</span>
                <div className="flex-1 h-px bg-[hsl(222,25%,18%)]" />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">I don't have the listing ID</label>
                <p className="text-slate-500 text-xs leading-relaxed">
                  If you can't find the listing ID, you can still submit a report. Describe the listing in the details field in Step 3 and our team will locate it.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleStep1}
                  className="w-full sm:flex-1 py-3 bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold rounded-lg transition-colors"
                >
                  Continue
                </button>
                <button
                  onClick={() => { setListingId('no-id'); setError(null); setStep(2); }}
                  className="w-full sm:flex-1 py-3 bg-white/5 hover:bg-white/10 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  I don't have the ID
                </button>
              </div>

              <div className="pt-2 border-t border-[hsl(222,25%,18%)]">
                <p className="text-slate-500 text-xs">
                  <ExternalLink className="w-3 h-3 inline mr-1" />
                  You can also report a listing directly from the listing page using the "Report" button.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Select Reason */}
          {step === 2 && (
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-4 sm:p-6 space-y-5">
              <div>
                <h2 className="text-base font-semibold text-white mb-1">Why are you reporting this listing?</h2>
                <p className="text-slate-400 text-xs">Select the reason that best describes the issue.</p>
              </div>

              <div className="space-y-2">
                {reportReasons.map((reason) => (
                  <button
                    key={reason.value}
                    onClick={() => { setSelectedReason(reason.value); setError(null); }}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${selectedReason === reason.value ? 'border-amber-400/50 bg-amber-400/5' : 'border-[hsl(222,25%,18%)] bg-[hsl(222,47%,6%)] hover:border-[hsl(222,25%,25%)]'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 transition-colors ${selectedReason === reason.value ? 'border-amber-400 bg-amber-400' : 'border-slate-600'}`} />
                      <div>
                        <p className={`text-sm font-medium ${selectedReason === reason.value ? 'text-white' : 'text-slate-300'}`}>{reason.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{reason.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { setStep(1); setError(null); }}
                  className="px-4 py-3 bg-white/5 hover:bg-white/10 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleStep2}
                  className="flex-1 py-3 bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold rounded-lg transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Add Details */}
          {step === 3 && (
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-4 sm:p-6 space-y-5">
              <div>
                <h2 className="text-base font-semibold text-white mb-1">Provide additional details</h2>
                <p className="text-slate-400 text-xs">The more detail you provide, the faster our team can act.</p>
              </div>

              {/* Summary */}
              <div className="bg-[hsl(222,47%,6%)] border border-[hsl(222,25%,18%)] rounded-lg p-4 space-y-2">
                <div className="flex justify-between gap-2 text-xs">
                  <span className="text-slate-500 flex-shrink-0">Listing</span>
                  <span className="text-slate-300 font-mono text-right truncate max-w-[180px] sm:max-w-[200px]">{listingId === 'no-id' ? 'Not provided' : listingId}</span>
                </div>
                <div className="flex justify-between gap-2 text-xs">
                  <span className="text-slate-500 flex-shrink-0">Reason</span>
                  <span className="text-slate-300 text-right">{reportReasons.find(r => r.value === selectedReason)?.label}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">
                  Details <span className="text-rose-400">*</span>
                </label>
                <textarea
                  value={details}
                  onChange={(e) => { setDetails(e.target.value); setError(null); }}
                  rows={6}
                  placeholder="Describe what you've seen. Include any relevant details such as: what makes you think the item is fake, where you've seen it sold elsewhere, what the seller said to you, etc."
                  className="w-full bg-[hsl(222,47%,6%)] border border-[hsl(222,25%,18%)] rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-400/50 transition-colors resize-none"
                />
                <p className="text-xs text-slate-600 mt-1">{details.length} characters</p>
              </div>

              <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-blue-300 text-xs leading-relaxed">
                    Your report is anonymous. The seller will not be told who reported them. False reports made in bad faith may result in action against your account.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { setStep(2); setError(null); }}
                  className="px-4 py-3 bg-white/5 hover:bg-white/10 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 py-3 bg-rose-500 hover:bg-rose-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {submitting ? 'Submitting…' : 'Submit Report'}
                </button>
              </div>
            </div>
          )}

          {/* Info box */}
          <div className="mt-6 bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Urgent Safety Concern?</p>
            <p className="text-slate-400 text-xs leading-relaxed mb-3">
              If you believe a listing poses an immediate safety risk or involves criminal activity, contact our trust team directly.
            </p>
            <a href="mailto:trust@drivenmarket.co.uk" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors">
              <Flag className="w-3.5 h-3.5" />
              trust@drivenmarket.co.uk
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default function ReportPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[hsl(222,47%,6%)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <ReportPageContent />
    </Suspense>
  );
}
